import { Text } from '@chatsdet/ui';
import type { CacheFormationDiagnostics } from '@chatsdet/types';

type Props = {
  diagnostics: CacheFormationDiagnostics;
};

export function CacheDebugFormationDiagnostics({ diagnostics }: Props) {
  const unresolvedSteps = diagnostics.playbook.steps.filter(
    (step) => step.actionRefCount > 0 && step.resolvedActionRefCount === 0
  );
  const uncoveredActions = diagnostics.locators.actions.filter(
    (action) =>
      action.requiresAgent ||
      !action.hasSelectors ||
      !action.hasPlaywrightLocator ||
      !!action.selectorAgentFailureReason
  );

  return (
    <div className="mb-3 rounded border border-gray-200 bg-gray-50/60 p-3 dark:border-gray-700 dark:bg-gray-900/20">
      <Text size="xs" weight="medium" className="text-muted-foreground mb-2">
        Formation Pipeline
      </Text>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <MetricBlock
          title="1. Playbook refs"
          rows={[
            ['steps', `${diagnostics.playbook.stepCount}`],
            ['with refs', `${diagnostics.playbook.stepsWithActionRefs}`],
            ['resolved', `${diagnostics.playbook.stepsWithResolvedActionRefs}`],
            ['unresolved', `${diagnostics.playbook.stepsWithUnresolvedActionRefs}`],
            ['gaps', `${diagnostics.playbook.gapCount}`],
          ]}
          tone={
            diagnostics.playbook.stepsWithUnresolvedActionRefs > 0 || diagnostics.playbook.gapCount > 0
              ? 'warning'
              : 'default'
          }
        />
        <MetricBlock
          title="2. Locator coverage"
          rows={[
            ['selector-capable', `${diagnostics.locators.selectorCapableActions}`],
            ['selectors', `${diagnostics.locators.actionsWithSelectors}`],
            ['playwright', `${diagnostics.locators.actionsWithPlaywrightLocator}`],
            ['agentic', `${diagnostics.locators.agenticLocatorActions}`],
            ['needs agent', `${diagnostics.locators.requiresAgentActions}`],
          ]}
          tone={diagnostics.locators.requiresAgentActions > 0 ? 'warning' : 'default'}
        />
        <MetricBlock
          title="3. Selector agent"
          rows={[
            ['status', formatSelectorAgentStatus(diagnostics.selectorAgent.status)],
            ['tasks', `${diagnostics.selectorAgent.totalTaskCount}`],
            ['live queue', `${diagnostics.selectorAgent.liveQueueTaskCount}`],
            ['proactive', `${diagnostics.selectorAgent.proactiveTaskCount}`],
            ['success', `${diagnostics.selectorAgent.successCount ?? 0}`],
            ['failure', `${diagnostics.selectorAgent.failureCount ?? 0}`],
          ]}
          tone={diagnostics.selectorAgent.status === 'failed' ? 'warning' : 'default'}
        />
      </div>

      {unresolvedSteps.length > 0 && (
        <div className="mt-3">
          <Text size="xs" weight="medium" className="text-muted-foreground mb-1">
            Unresolved action refs
          </Text>
          <div className="space-y-1">
            {unresolvedSteps.slice(0, 5).map((step) => (
              <div key={step.order} className="text-xs text-amber-700 dark:text-amber-300">
                {step.order}. {step.action} {step.target}
              </div>
            ))}
          </div>
        </div>
      )}

      {uncoveredActions.length > 0 && (
        <div className="mt-3">
          <Text size="xs" weight="medium" className="text-muted-foreground mb-1">
            Locator gaps
          </Text>
          <div className="space-y-1">
            {uncoveredActions.slice(0, 5).map((action) => (
              <div key={`${action.index}:${action.actionType}`} className="text-xs text-muted-foreground">
                #{action.index} {action.actionType}
                {action.elementDescription ? ` · ${action.elementDescription}` : ''}
                {action.selectorAgentFailureReason ? ` · ${action.selectorAgentFailureReason}` : ''}
              </div>
            ))}
          </div>
        </div>
      )}

      {diagnostics.selectorAgent.failureReasons && diagnostics.selectorAgent.failureReasons.length > 0 && (
        <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-300">
          {diagnostics.selectorAgent.failureReasons.join(' · ')}
        </div>
      )}
    </div>
  );
}

function MetricBlock({
  title,
  rows,
  tone,
}: {
  title: string;
  rows: Array<[string, string]>;
  tone: 'default' | 'warning';
}) {
  return (
    <div
      className={
        tone === 'warning'
          ? 'rounded border border-amber-200 bg-amber-50 p-2 dark:border-amber-900/50 dark:bg-amber-950/20'
          : 'rounded border border-gray-200 bg-white/80 p-2 dark:border-gray-700 dark:bg-gray-950/30'
      }
    >
      <div className="text-xs font-medium text-foreground mb-1.5">{title}</div>
      <div className="space-y-1">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-center justify-between gap-3 text-xs">
            <span className="text-muted-foreground">{label}</span>
            <span className="font-medium text-foreground text-right">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatSelectorAgentStatus(status: CacheFormationDiagnostics['selectorAgent']['status']) {
  switch (status) {
    case 'not-needed':
      return 'not needed';
    case 'pending':
      return 'pending';
    case 'queued':
      return 'queued';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    default:
      return status;
  }
}
